import React, { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import type { FeatureCollection } from 'geojson';
import type { AirportTopology } from '../../types/conops';
import type {
  UnifiedAirportMapProps,
  LayerId,
  MapEngineContext as MapEngineCtx,
} from '../../types/mapEngine';
import { MapEngineProvider } from './MapEngineContext';
import { MAP_MODES } from './modes/mapModes';
import { useMapStore } from '../../store/mapStore';
import { conopsService } from '../../services/conops';

// Layer components
import BaseAirportLayer from './layers/BaseAirportLayer';
import HeatmapLineLayer from './layers/HeatmapLineLayer';
import CongestionLayer from './layers/CongestionLayer';
import PushbackRiskLayer from './layers/PushbackRiskLayer';
import SweptPathLayer from './layers/SweptPathLayer';
import SafetyEnvelopeLayer from './layers/SafetyEnvelopeLayer';
import JetBlastLayer from './layers/JetBlastLayer';
import AircraftDeckLayer from './aircraft/AircraftDeckLayer';
import MapLayerPanel from './MapLayerPanel';
import MapModeToolbar from './MapModeToolbar';

// ---------------------------------------------------------------------------
// Extended props
// ---------------------------------------------------------------------------

interface AviPLANData {
  sweptPaths?: FeatureCollection | null;
  safetyEnvelopes?: FeatureCollection | null;
  jetBlast?: FeatureCollection | null;
}

interface ExtendedProps extends UnifiedAirportMapProps {
  showLayerPanel?: boolean;
  heatmapData?: {
    taxiRisk?: FeatureCollection | null;
    taxiSpeed?: FeatureCollection | null;
    ciGraph?: FeatureCollection | null;
    tcfGraph?: FeatureCollection | null;
    mhtGraph?: FeatureCollection | null;
    congestion?: FeatureCollection | null;
    arrivals?: FeatureCollection | null;
    departures?: FeatureCollection | null;
    pushback?: FeatureCollection | null;
  };
  aviplanData?: AviPLANData;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const UnifiedAirportMap: React.FC<ExtendedProps> = ({
  mode,
  icaoCode,
  initialCenter,
  initialZoom,
  layerOverrides,
  aircraft,
  onMapReady,
  onAircraftClick,
  showTrails,
  children,
  className,
  style,
  showLayerPanel,
  heatmapData,
  aviplanData,
  topologyRefreshKey,
}) => {
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const [topology, setTopology] = useState<AirportTopology | null>(null);

  // Pull shared state from Zustand store
  const storeMode        = useMapStore((s) => s.mode);
  const layerVisibility  = useMapStore((s) => s.layerVisibility);
  const baseStyle        = useMapStore((s) => s.baseStyle);
  const setMode            = useMapStore((s) => s.setMode);
  const setLayerVisibility = useMapStore((s) => s.setLayerVisibility);
  const storeSetBaseStyle  = useMapStore((s) => s.setBaseStyle);

  // ---------------------------------------------------------------------------
  // Map initialization — runs once
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!mapContainer.current) return;

    mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN as string;
    const modeConfig = MAP_MODES[mode];

    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: modeConfig.mapboxStyle,
      center: initialCenter ?? modeConfig.defaultCenter,
      zoom: initialZoom ?? modeConfig.defaultZoom,
      attributionControl: false,
      logoPosition: 'bottom-left',
    });

    map.addControl(
      new mapboxgl.NavigationControl({ showCompass: true }),
      'bottom-right',
    );
    map.addControl(
      new mapboxgl.ScaleControl({ unit: 'metric' }),
      'bottom-left',
    );
    map.addControl(
      new mapboxgl.AttributionControl({ compact: true }),
      'bottom-left',
    );

    const addCadBackground = () => {
      if (!map.getLayer('me-cad-bg')) {
        map.addLayer({ id: 'me-cad-bg', type: 'background',
          paint: { 'background-color': '#0d1117' } });
      }
    };

    map.on('load', () => {
      addCadBackground();
      setMapLoaded(true);
      onMapReady?.(map);
    });

    // Re-raise mapLoaded after a style swap so layer components reinit
    map.on('style.load', () => {
      addCadBackground();
      setMapLoaded(true);
    });

    mapRef.current = map;

    return () => {
      map.remove();
      setMapLoaded(false);
    };
    // Stable — only runs once. aipRasterBlobUrl cleanup handled via ref on unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------------
  // Topology loading
  // ---------------------------------------------------------------------------

  useEffect(() => {
    conopsService.getTopology(icaoCode).then(setTopology).catch(() => {});
  }, [icaoCode, topologyRefreshKey]);

  // ---------------------------------------------------------------------------
  // Base style switching
  // ---------------------------------------------------------------------------

  const handleSetBaseStyle = useCallback((style: string) => {
    if (!mapRef.current) return;
    storeSetBaseStyle(style);
    setMapLoaded(false);
    requestAnimationFrame(() => mapRef.current?.setStyle(style));
  }, [storeSetBaseStyle]);

  // ---------------------------------------------------------------------------
  // Mode + layer-override sync when the `mode` prop changes
  // ---------------------------------------------------------------------------

  useEffect(() => {
    setMode(mode);
    if (layerOverrides) {
      Object.entries(layerOverrides).forEach(([id, vis]) =>
        setLayerVisibility(id as LayerId, vis as boolean),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // ---------------------------------------------------------------------------
  // Build context value
  // ---------------------------------------------------------------------------

  const ctxValue: MapEngineCtx = {
    map: mapRef.current,
    mapLoaded,
    mode: storeMode,
    baseStyle,
    setBaseStyle: handleSetBaseStyle,
    layerVisibility,
    setLayerVisibility,
    aircraft: aircraft ?? [],
    topology: topology
      ? {
          taxiways: topology.taxiways,
          stands: topology.stands,
          aipStands: topology.aip_stands ?? null,
          importedTopology: topology.imported_topology ?? null,
        }
      : null,
    icaoCode,
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <MapEngineProvider value={ctxValue}>
      <div
        className={`relative w-full h-full ${className ?? ''}`}
        style={style}
      >
        {/* Base Mapbox GL container */}
        <div ref={mapContainer} className="absolute inset-0" />

        {/* Layer components — only mounted after the map is loaded */}
        {mapLoaded && mapRef.current && (
          <>
            <BaseAirportLayer
              map={mapRef.current}
              topology={ctxValue.topology}
              layerVisibility={layerVisibility}
            />
            <HeatmapLineLayer
              map={mapRef.current}
              taxiRiskData={heatmapData?.taxiRisk ?? null}
              taxiSpeedData={heatmapData?.taxiSpeed ?? null}
              ciGraphData={heatmapData?.ciGraph ?? null}
              tcfGraphData={heatmapData?.tcfGraph ?? null}
              mhtGraphData={heatmapData?.mhtGraph ?? null}
              layerVisibility={layerVisibility}
            />
            <CongestionLayer
              map={mapRef.current}
              congestionData={heatmapData?.congestion ?? null}
              arrivalsData={heatmapData?.arrivals ?? null}
              departuresData={heatmapData?.departures ?? null}
              layerVisibility={layerVisibility}
            />
            <PushbackRiskLayer
              map={mapRef.current}
              pushbackData={heatmapData?.pushback ?? null}
              visible={layerVisibility['heatmap-pushback-risk'] ?? false}
            />
            <SweptPathLayer
              map={mapRef.current}
              data={aviplanData?.sweptPaths ?? null}
              visible={layerVisibility['aviplan-swept-paths'] ?? false}
            />
            <SafetyEnvelopeLayer
              map={mapRef.current}
              data={aviplanData?.safetyEnvelopes ?? null}
              visible={layerVisibility['aviplan-safety-envelopes'] ?? false}
            />
            <JetBlastLayer
              map={mapRef.current}
              data={aviplanData?.jetBlast ?? null}
              visible={layerVisibility['aviplan-jet-blast'] ?? false}
            />
            <AircraftDeckLayer
              map={mapRef.current}
              aircraft={aircraft ?? []}
              visible={layerVisibility['aircraft-silhouettes'] ?? false}
              showTrails={showTrails}
              onAircraftClick={onAircraftClick}
            />
          </>
        )}

        {/* Base style switcher + layer panel */}
        {showLayerPanel !== false && <MapModeToolbar />}
        {showLayerPanel !== false && <MapLayerPanel />}

        {/* Slot for consumer-supplied overlays / panels */}
        {children}
      </div>
    </MapEngineProvider>
  );
};

export default UnifiedAirportMap;
